import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Check, Loader2, Pen, X } from 'lucide-react';
import { SignatureCapture } from './SignatureCapture';

interface Signature {
  role: 'recording_secretary' | 'president' | 'treasury_secretary';
  name: string;
  data?: string;
  date?: string;
}

interface FinalizeWithSignaturesProps {
  documentTitle?: string;
  /** Optional custom description shown under the title. Defaults to the generic finalize copy. */
  description?: string;
  recordingSecretaryName?: string;
  presidentName?: string;
  treasurySecretaryName?: string;
  /** Dynamic role label for the primary officer, defaults to "Secretary Treasurer" */
  primaryOfficerRoleLabel?: string;
  requiredSigners?: ('recording_secretary' | 'president' | 'treasury_secretary')[];
  onFinalize: (signatures: {
    recording_secretary_signature?: string;
    recording_secretary_signature_date?: string;
    president_signature?: string;
    president_signature_date?: string;
    treasury_secretary_signature?: string;
    treasury_secretary_signature_date?: string;
  }) => void;
  onCancel: () => void;
}

export function FinalizeWithSignatures({
  documentTitle = 'Finalize Document',
  description,
  recordingSecretaryName,
  presidentName,
  treasurySecretaryName,
  primaryOfficerRoleLabel = 'Secretary Treasurer',
  requiredSigners = ['recording_secretary', 'president'],
  onFinalize,
  onCancel,
}: FinalizeWithSignaturesProps) {
  const [signatures, setSignatures] = useState<{
    recording_secretary?: Signature;
    president?: Signature;
    treasury_secretary?: Signature;
  }>({});

  const [activeCapture, setActiveCapture] = useState<'recording_secretary' | 'president' | 'treasury_secretary' | null>(
    null
  );

  const [isFinalizing, setIsFinalizing] = useState(false);

  const getSignerName = (role: 'recording_secretary' | 'president' | 'treasury_secretary') => {
    if (role === 'recording_secretary') return recordingSecretaryName || '';
    if (role === 'president') return presidentName || '';
    return treasurySecretaryName || '';
  };

  const handleSignatureSave = (role: 'recording_secretary' | 'president' | 'treasury_secretary', data: string) => {
    const now = new Date().toISOString().split('T')[0];
    setSignatures((prev) => ({
      ...prev,
      [role]: {
        role,
        name: getSignerName(role),
        data,
        date: now,
      },
    }));
    setActiveCapture(null);
  };

  const handleClearSignature = (role: 'recording_secretary' | 'president' | 'treasury_secretary') => {
    setSignatures((prev) => ({
      ...prev,
      [role]: undefined,
    }));
  };

  const allSignaturesComplete = requiredSigners.every((role) => signatures[role]?.data);

  const handleFinalize = async () => {
    if (!allSignaturesComplete || isFinalizing) return;

    setIsFinalizing(true);
    try {
      const result: any = {};
      requiredSigners.forEach((role) => {
        result[`${role}_signature`] = signatures[role]!.data!;
        result[`${role}_signature_date`] = signatures[role]!.date!;
      });

      await onFinalize(result);
    } catch (error) {
      console.error('Finalization error:', error);
    } finally {
      setIsFinalizing(false);
    }
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onCancel}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{documentTitle}</DialogTitle>
            <DialogDescription>
              {description ?? 'These people need to sign before this document can be closed.'}
            </DialogDescription>
          </DialogHeader>

          <div className={`grid gap-4 ${requiredSigners.length > 1 ? 'md:grid-cols-2' : 'max-w-md mx-auto'}`}>
            {requiredSigners.includes('recording_secretary') && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recording Secretary</CardTitle>
                  <p className="text-sm text-muted-foreground">{recordingSecretaryName}</p>
                </CardHeader>
                <CardContent>
                  {signatures.recording_secretary?.data ? (
                    <div className="space-y-3">
                      <div className="border-2 border-green-200 rounded-lg p-3 bg-green-50 dark:bg-green-950 dark:border-green-800">
                        <img
                          src={signatures.recording_secretary.data}
                          alt="Recording Secretary Signature"
                          className="w-full h-20 object-contain"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                          <Check className="w-4 h-4 mr-1" />
                          Signed {signatures.recording_secretary.date}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleClearSignature('recording_secretary')}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-muted rounded-lg p-8 text-center bg-muted/50">
                        <Pen className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Not yet signed</p>
                      </div>
                      <Button
                        className="w-full min-h-[44px]"
                        onClick={() => setActiveCapture('recording_secretary')}
                      >
                        <Pen className="w-4 h-4 mr-2" />
                        Sign as Recording Secretary
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {requiredSigners.includes('president') && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">President</CardTitle>
                  <p className="text-sm text-muted-foreground">{presidentName}</p>
                </CardHeader>
                <CardContent>
                  {signatures.president?.data ? (
                    <div className="space-y-3">
                      <div className="border-2 border-green-200 rounded-lg p-3 bg-green-50 dark:bg-green-950 dark:border-green-800">
                        <img
                          src={signatures.president.data}
                          alt="President Signature"
                          className="w-full h-20 object-contain"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                          <Check className="w-4 h-4 mr-1" />
                          Signed {signatures.president.date}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleClearSignature('president')}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-muted rounded-lg p-8 text-center bg-muted/50">
                        <Pen className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Not yet signed</p>
                      </div>
                      <Button
                        className="w-full min-h-[44px]"
                        onClick={() => setActiveCapture('president')}
                      >
                        <Pen className="w-4 h-4 mr-2" />
                        Sign as President
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {requiredSigners.includes('treasury_secretary') && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{primaryOfficerRoleLabel}</CardTitle>
                  <p className="text-sm text-muted-foreground">{treasurySecretaryName}</p>
                </CardHeader>
                <CardContent>
                  {signatures.treasury_secretary?.data ? (
                    <div className="space-y-3">
                      <div className="border-2 border-green-200 rounded-lg p-3 bg-green-50 dark:bg-green-950 dark:border-green-800">
                        <img
                          src={signatures.treasury_secretary.data}
                          alt={`${primaryOfficerRoleLabel} Signature`}
                          className="w-full h-20 object-contain"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-green-600 dark:text-green-400">
                          <Check className="w-4 h-4 mr-1" />
                          Signed {signatures.treasury_secretary.date}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleClearSignature('treasury_secretary')}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Clear
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-muted rounded-lg p-8 text-center bg-muted/50">
                        <Pen className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Not yet signed</p>
                      </div>
                      <Button
                        className="w-full min-h-[44px]"
                        onClick={() => setActiveCapture('treasury_secretary')}
                      >
                        <Pen className="w-4 h-4 mr-2" />
                        Sign as {primaryOfficerRoleLabel}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" onClick={onCancel} disabled={isFinalizing}>
              Cancel
            </Button>
            <Button onClick={handleFinalize} disabled={!allSignaturesComplete || isFinalizing}>
              {isFinalizing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Finalizing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Finalize Document
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Capture Modal */}
      {activeCapture && (
        <SignatureCapture
          role={activeCapture}
          signerName={getSignerName(activeCapture)}
          onSave={(data) => handleSignatureSave(activeCapture, data)}
          onCancel={() => setActiveCapture(null)}
        />
      )}
    </>
  );
}
